import { forwardRef } from "react";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";
export type ButtonSize = "default" | "sm" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const base =
  "inline-flex items-center justify-center gap-2 rounded-md font-display tracking-wide " +
  "transition-colors disabled:opacity-60 disabled:pointer-events-none " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-background";

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-primary-foreground hover:bg-rust-600 active:translate-y-px shadow-sm",
  secondary:
    "bg-secondary text-secondary-foreground hover:bg-ochre-600 active:translate-y-px shadow-sm",
  ghost:
    "bg-transparent text-foreground hover:bg-sand-100 active:bg-sand-200",
  destructive:
    "bg-destructive text-destructive-foreground hover:opacity-90 active:translate-y-px shadow-sm",
};

const sizes: Record<ButtonSize, string> = {
  default: "h-11 px-5 text-base",
  sm: "h-9 px-3 text-sm",
  lg: "h-14 px-7 text-lg",
};

export function buttonStyles(
  variant: ButtonVariant = "primary",
  size: ButtonSize = "default",
  className?: string,
): string {
  return cn(base, variants[variant], sizes[size], className);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "default", type = "button", ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={buttonStyles(variant, size, className)}
      {...rest}
    />
  ),
);
Button.displayName = "Button";
